import React, { useMemo, useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import {
  useTable,
  useSortBy,
  useRowSelect,
  useFlexLayout,
  useResizeColumns
} from 'react-table';
import { ChevronDown, ChevronUp } from 'react-feather';
import { Element } from 'react-scroll';

import { TableWrapper } from './TableV2.style';
import { generateRowId, tryParseJson } from 'lib/utils/helper';
import { Text } from '..';

export const TableV2 = ({
  columns,
  data,
  sectionTitle,
  setRowsToExport,
  resetRows
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const params = queryString.parse(searchParams.toString());

  const trimStr = (str, delimiter) => {
    if (typeof str !== 'string') return str;

    let _delimiter = '';

    if (typeof delimiter === 'string') {
      _delimiter = delimiter;
    }

    return str.split(/\s+/g).join(_delimiter);
  };

  const isMarked = (id) => {
    const rowId = params.rowId;
    if (rowId) {
      return trimStr(id) === trimStr(rowId);
    }

    const rowIds = tryParseJson(params.rowIds);

    if (rowIds?.length) {
      return rowIds.includes(id);
    }

    return false;
  };

  const _columns = useMemo(() => {
    const PADDING = 320;
    const totalAvailableWidth = window.innerWidth - PADDING;
    const avgLengths = {};

    columns.forEach((col) => {
      const headerLen = col.length;
      const { totalLen, maxContentLen } = data.reduce(
        (acc, row) => {
          const length = (row[col] ?? '').length;
          acc.totalLen += length;
          if (length > acc.maxContentLen) acc.maxContentLen = length;

          return acc;
        },
        { totalLen: 0, maxContentLen: 0 }
      );
      const avgLen = data.length > 0 ? totalLen / data.length : 0;
      const effectiveLen = Math.max(headerLen, Math.min(maxContentLen, avgLen * 1.5));
      avgLengths[col] = effectiveLen;
    });

    const scaledLengths = {};

    columns.forEach((col) => {
      scaledLengths[col] = Math.sqrt(avgLengths[col]);
    });

    const totalScaled = Object.values(scaledLengths).reduce((a, b) => a + b, 0);
    const minColWidth = 100;
    const maxColWidth = totalAvailableWidth * 0.7;
    const checkboxColWidth = 31;
    const usableWidth = totalAvailableWidth - checkboxColWidth;
    const colSizes = {};

    columns.forEach((col) => {
      const proportion = scaledLengths[col] / totalScaled;
      const calculatedWidth = Math.round(proportion * usableWidth);
      colSizes[col] = Math.max(minColWidth, Math.min(maxColWidth, calculatedWidth));
    });

    return columns.map((col) => ({
      width: colSizes[col],
      Header: <Text section={sectionTitle} inheritStyles={true} key={`${sectionTitle}_${col}_text_header`}>{col}</Text>,
      id: col,
      accessor: (row) => row[col],
      Cell: ({ value, row }) => <Text rowId={generateRowId(sectionTitle, row.values, row.index)} section={sectionTitle} inheritStyles={true} key={`${sectionTitle}_${row.index}_${col}_text`}>{value}</Text>
    }));
  }, []);
  const _data = useMemo(() => data, []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    selectedFlatRows,
    toggleAllRowsSelected
  } = useTable(
    { columns: _columns, data: _data, autoResetSelectedRows: true },
    useSortBy,
    useResizeColumns,
    useRowSelect,
    useFlexLayout,
    (hooks) => {
      hooks.allColumns.push((columns) => [
        // Let's make a column for selection
        {
          id: 'selection',
          disableResizing: true,
          minWidth: 31,
          width: 31,
          maxWidth: 31,
          Header: ({ getToggleAllRowsSelectedProps }) => (
            <div>
              <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
            </div>
          ),
          // The cell can use the individual row's getToggleRowSelectedProps method
          // to the render a checkbox
          Cell: ({ row }) => (
            <div>
              <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
            </div>
          )
        },
        ...columns
      ]);
      hooks.useInstanceBeforeDimensions.push(({ headerGroups }) => {
        // fix the parent group of the selection button to not be resizable
        const selectionGroupHeader = headerGroups[0].headers[0];
        selectionGroupHeader.canResize = false;
      });
    }
  );

  useEffect(() => {
    const selectedRows =
      selectedFlatRows && selectedFlatRows.length && !resetRows
        ? selectedFlatRows.map((row) => row.original)
        : [];

    setRowsToExport((prev) => {
      if (!selectedRows.length) {
        return prev.filter((el) => el.sectionTitle !== sectionTitle);
      }

      let newArr = [];

      const newRowsData = {
        sectionTitle: sectionTitle,
        rows: selectedRows
      };

      const hasSection = prev.find(
        (el) => el.sectionTitle === newRowsData.sectionTitle
      );

      if (prev.length && !hasSection) {
        newArr.push(...prev);
        newArr.push(newRowsData);

        return newArr;
      } else if (!prev.length && !hasSection) {
        newArr.push(newRowsData);

        return newArr;
      }

      prev.map((row) => {
        if (row.sectionTitle === newRowsData.sectionTitle) {
          newArr.push({
            sectionTitle: row.sectionTitle,
            rows: newRowsData.rows
          });
        } else {
          newArr.push(row);
        }
      });

      return newArr;
    });

    if (resetRows) {
      toggleAllRowsSelected(false);
    }
  }, [selectedFlatRows, resetRows]);

  const IndeterminateCheckbox = React.forwardRef(
    ({ indeterminate, ...rest }, ref) => {
      const defaultRef = React.useRef();
      const resolvedRef = ref || defaultRef;

      React.useEffect(() => {
        resolvedRef.current.indeterminate = indeterminate;
      }, [resolvedRef, indeterminate]);

      return (
        <>
          <input type="checkbox" ref={resolvedRef} {...rest} />
        </>
      );
    }
  );

  const removeRowId = (id) => {
    const isMarkedRow = isMarked(id);
    if (isMarkedRow) {
      const queryParams = new URLSearchParams(searchParams);
      queryParams.delete('rowId');
      queryParams.delete('rowIds');
      navigate(
        {
          search: queryParams.toString()
        },
        { replace: true }
      );
    }
  };

  return (
    <>
      <TableWrapper {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup, i) => (
            <tr key={`headerGroup${i}`} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th key={`${column.id}${i}`} {...column.getHeaderProps()}>
                  <div>
                    <div
                      className="header-content"
                      {...column.getSortByToggleProps()}
                    >
                      {column.render('Header')}
                      <span>
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <>
                              {' '}
                              <ChevronDown size={12} />
                            </>
                          ) : (
                            <>
                              {' '}
                              <ChevronUp size={12} />
                            </>
                          )
                        ) : (
                          ''
                        )}
                      </span>
                    </div>
                    {column.canResize && (
                      <div
                        {...column.getResizerProps()}
                        className={`resizer ${
                          column.isResizing ? 'isResizing' : ''
                        }`}
                      />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row, rowIndex) => {
            prepareRow(row);

            const rowId = generateRowId(sectionTitle, row.values, rowIndex);

            return (
              <Element key={rowId} name={rowId}>
                <tr
                  {...row.getRowProps()}
                  className={`${isMarked(rowId) ? 'isMarked' : ''} ${
                    row.isSelected ? 'selected' : ''
                  }`}
                  onClick={() => removeRowId(rowId)}
                >
                  {row.cells.map((cell) => {
                    return (
                      <td {...cell.getCellProps()}>
                        {cell.render('Cell')}
                      </td>
                    );
                  })}
                </tr>
              </Element>
            );
          })}
        </tbody>
      </TableWrapper>
    </>
  );
};
